import { Injectable } from '@angular/core';
import { EntityState, EntityStore, StoreConfig } from '@datorama/akita';
import { TranslateService } from '@ngx-translate/core';
import { Observable, defer, of } from 'rxjs';
import { map, shareReplay, startWith } from 'rxjs/operators';

import { SectionType } from 'phase/model/section/section.type';
import { Section } from 'phase/section/section';
import { SectionI18n } from 'phase/section/section-i18n';

import { AppLocator } from '../../../app.locator';

export interface SectionI18nDocument {
	readonly name: string;
	readonly locale: string;
}

export interface SectionDocument {
	readonly id: string;
	readonly type: string;
	readonly phaseId: string;
	readonly position: number;
	readonly updateTimestamp: number;
	readonly localizations: SectionI18nDocument[];
}

export type SectionState = EntityState<SectionDocument, string>;

@Injectable({ providedIn: 'root' })
@StoreConfig({ name: 'sections' })
export class SectionStore extends EntityStore<SectionState> {
	constructor() {
		super();
	}

	public get _translateService(): TranslateService {
		return AppLocator.inject(TranslateService);
	}

	public asDocument(section: Section): SectionDocument {
		return section
			? {
					id: section.id,
					type: section.type,
					phaseId: section.phaseId,
					position: section.position,
					updateTimestamp: section.updateTimestamp,
					localizations: section.localizations.map((loc) => ({
						locale: loc.locale,
						name: loc.name,
					})),
			  }
			: null;
	}

	public asEntity(document: SectionDocument): Section {
		const localizations = !!document
			? (document.localizations || []).map(
					(loc) => new SectionI18n(loc.name, loc.locale)
			  )
			: [];

		return document
			? new Section(
					document.id,
					document.phaseId,
					document.position,
					SectionType[document.type],
					document.updateTimestamp,
					localizations,
					this._observeLocalization(localizations)
			  )
			: null;
	}

	private _observeLocalization<T extends { locale: string }>(
		localizations: ReadonlyArray<T>
	): Observable<T> {
		return localizations && localizations.length > 0
			? defer(() =>
					this._translateService.onLangChange.pipe(
						map((e) => e.lang),
						startWith(this._translateService.currentLang)
					)
			  ).pipe(
					map((lang) => localizations.find((loc) => loc.locale === lang)),
					map((loc) => (!loc ? localizations[0] : loc)),
					shareReplay({ bufferSize: 1, refCount: true })
			  )
			: of(null);
	}
}
